import React, { useState, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Divider, Alert,
    TextField, Button, Box, Select, MenuItem, FormControl, InputLabel, IconButton, Grid, Chip
} from '@mui/material';
import UploadFile from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDropzone } from 'react-dropzone';
import { api } from "../../../services/Main/Api";
import { useSnackbar } from "notistack";
import { getCondoId } from "../../../services/Main/AuthStorage";
import Masks from "../../../components/Shared/Masks";

const OPERATION_TYPES = [
    { value: "40", label: "Transferência PIX" },
    { value: "3", label: "Transferência TED" }
];

const LIMIT_TYPES = [
    { value: 0, label: "Limite Diário" },
    // { value: 1, label: "Limite Semanal" },
    { value: 2, label: "Limite Mensal" },
    { value: 3, label: "Limite Noturno" },
    { value: 4, label: "Limite por Transação" },
    { value: 5, label: "Limite Diário para Mesma Titularidade" }
];

export default function RequestLimitForm({ open, onClose }) {
    const [files, setFiles] = useState([]);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const companyId = getCondoId();

    // Lista dinâmica de campos
    const [limits, setLimits] = useState([
        { operationType: '', type: '', value: '' }
    ]);

    const isDisabled =
        files.length === 0 ||
        limits.some(
            item => !item.operationType || item.type === '' || !item.value
        );

    // Dropzone config
    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles && acceptedFiles.length > 0) {
            // Evita arquivos duplicados
            setFiles(prev =>
                [...prev, ...acceptedFiles.filter(f => !prev.some(p => p.name === f.name && p.size === f.size))]
            );
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/pdf': [],
            'image/*': []
        },
        multiple: true
    });

    const handleRemoveFile = (index) => {
        setFiles(files => files.filter((_, i) => i !== index));
    };

    const handleLimitChange = (idx, field, value) => {
        setLimits(limits =>
            limits.map((item, i) =>
                i === idx ? { ...item, [field]: value } : item
            )
        );
    };

    const handleAddLimit = () => {
        setLimits([...limits, { operationType: '', type: '', value: '' }]);
    };

    const handleRemoveLimit = (idx) => {
        setLimits(limits => limits.filter((_, i) => i !== idx));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const formData = new FormData();

        // Adiciona os arquivos
        files.forEach(file => {
            formData.append('document_files', file);
        });

        // Adiciona os limites (como lista)
        formData.append('limits', JSON.stringify(
            limits.map(item => ({
                operation_type: item.operationType,
                type: item.type,
                value: item.value.replace(/[^0-9]/g, '')
            }))
        ));

        formData.append('company', companyId);

        try {
            await api.post(`fitbank/limit/request/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            enqueueSnackbar('Sucesso ao enviar sua solicitação!', { variant: 'success' });
            setFiles([]);
            setLimits([{ operationType: '', type: '', value: '' }]);
        } catch (error) {
            const errorMessage = error.response?.data[0] || 'Erro ao enviar sua solicitação!';
            console.log(error.response?.data[0], error);
            enqueueSnackbar(errorMessage, { variant: 'error' });
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Solicitar Aumento de Limite</DialogTitle>
            <Divider sx={{ mt: '.5rem' }} />
            <DialogContent sx={{ minWidth: 600 }}>
                {message && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {message}
                    </Alert>
                )}
                <form onSubmit={handleSubmit}>
                    {/* Dropzone no topo */}
                    <Box
                        {...getRootProps()}
                        sx={{
                            border: '2px dotted gray',
                            borderRadius: 1,
                            padding: '10px',
                            textAlign: 'center',
                            marginBottom: 3,
                            cursor: 'pointer',
                            backgroundColor: isDragActive ? '#e0e0e0' : '#f3f3f3',
                            transition: 'background 0.2s'
                        }}
                    >
                        <input {...getInputProps()} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <UploadFile sx={{ color: 'gray' }} />
                            <span style={{ color: 'gray' }}>
                                {isDragActive
                                    ? 'Solte os arquivos aqui...'
                                    : files.length === 0
                                        ? 'Comprovante(s) de Faturamento'
                                        : `${files.length} arquivo(s) selecionado(s)`}
                            </span>
                        </Box>
                        {/* Lista de arquivos */}
                        {files.length > 0 && (
                            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                                {files.map((file, idx) => (
                                    <Chip
                                        key={file.name + file.size}
                                        label={file.name}
                                        onDelete={e => {
                                            e.stopPropagation();
                                            handleRemoveFile(idx);
                                        }}
                                        deleteIcon={<DeleteIcon />}
                                        sx={{ maxWidth: 200 }}
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>

                    {/* Lista dinâmica de campos */}
                    <Grid container spacing={2}>
                        {limits.map((item, idx) => (
                            <React.Fragment key={idx}>
                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Operação</InputLabel>
                                        <Select
                                            value={item.operationType}
                                            label="Operação"
                                            onChange={e =>
                                                handleLimitChange(idx, 'operationType', e.target.value)
                                            }
                                        >
                                            {OPERATION_TYPES.map(opt => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={5}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Tipo</InputLabel>
                                        <Select
                                            value={item.type}
                                            label="Tipo"
                                            onChange={e =>
                                                handleLimitChange(idx, 'type', e.target.value)
                                            }
                                        >
                                            {LIMIT_TYPES.map(opt => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={10} md={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Valor"
                                        value={Masks.money(item.value.toString())}
                                        onChange={e =>
                                            handleLimitChange(idx, 'value', e.target.value.replace(/\D/g, ''))
                                        }
                                        placeholder="R$ 0,00"
                                    />
                                </Grid>
                                <Grid item xs={2} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
                                    {limits.length > 1 && (
                                        <IconButton
                                            color="error"
                                            onClick={() => handleRemoveLimit(idx)}
                                            size="small"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                                </Grid>
                            </React.Fragment>
                        ))}
                    </Grid>

                    <Box sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="outlined"
                            onClick={handleAddLimit}
                            size="small"
                        >
                            Adicionar Limite
                        </Button>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <DialogActions sx={{ justifyContent: 'space-between', p: 0, mt: 2 }}>
                        <Button onClick={onClose} size="small" color="primary" disabled={loading}>
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            type="submit"
                            size="small"
                            color="primary"
                            disabled={isDisabled || loading}
                        >
                            {loading ? 'Enviando...' : 'Solicitar'}
                        </Button>
                    </DialogActions>
                </form>
            </DialogContent>
        </Dialog>
    );
}